/**
 * 補正サーバー（live-correct。Issue #626）を見る画面の共通の道具（Issue #722 でまとめた）。
 * 補正サーバーは CloudFront の `/live/*` で同じ origin にいる。
 *
 * - `fetchLiveInfo` / `useLiveInfo` — `/live/info.json`（大きさ・ゾーン・最後にフレームが届いた時刻・座標の指紋 cams_hash と無い理由 cams_missing・録画中か）
 * - `isLiveFresh` — カメラの配信が届いているか（最後のフレームが `LIVE_FRESH_SEC` 秒以内）
 * - `useLiveVideo` — `/live/stream?…` を動画で canvas に描く（入力画面と同じ LiveVideo＝WebCodecs）
 * - `useStill` — 読み終わってから差し替える静止画（ちらつかない。`/live/raw.jpg` など）
 *
 * 使う画面: 補正サーバーの起動・停止（LiveServerPanel）・TetherCam の一覧（pages/TetherCamDevices.js。配信の動画 LiveStream）・
 * 座標の編集（QuadSetupEditor）・入力画面のゾーン（pages/OcgInput.js）。
 */
import { liveUrl } from './liveBase';
import { useEffect, useRef, useState } from 'react';
import LiveVideo from './ocgInput/liveVideo';

export const LIVE_INFO_URL = '/live/info.json';
/** これより新しいフレームが届いていれば「配信が届いている」 */
export const LIVE_FRESH_SEC = 10;

/** カメラの配信が届いているか（補正サーバーの時計は数秒ずれていることがあるので絶対値で見る） */
export const isLiveFresh = (info, nowSec = Date.now() / 1000) => (
  !!info?.last_frame_unix && Math.abs(nowSec - info.last_frame_unix) < LIVE_FRESH_SEC
);

/** `/live/info.json` を 1 回読む（読めなければ null。投げない） */
export async function fetchLiveInfo(fetchImpl = (...a) => fetch(...a)) {
  try {
    const r = await fetchImpl(liveUrl(LIVE_INFO_URL), { cache: 'no-store' });
    return r.ok ? await r.json() : null;
  } catch {
    return null;
  }
}

/**
 * `/live/info.json` を `intervalMs` ごとに読み直す。`fetchImpl` が無ければ読まない（テストの jsdom・VideoDecoder の無い端末）。
 * `restartKey` が変わったらその場で読み直して数え直す（見るものを切り替えたとき）。
 * @returns `{ info, at }` —— info は読むまで undefined・読めなければ null、at は最後に読み終えた時刻（ms。静止画の読み直しの鍵）
 */
export function useLiveInfo(fetchImpl, intervalMs, restartKey = null) {
  const [state, setState] = useState({ info: undefined, at: 0 });
  useEffect(() => {
    if (!fetchImpl) return undefined;
    let alive = true;
    let timer;
    const tick = async () => {
      const info = await fetchLiveInfo(fetchImpl);
      if (!alive) return;
      setState({ info, at: Date.now() });
      timer = setTimeout(tick, intervalMs);
    };
    tick();
    return () => { alive = false; clearTimeout(timer); };
  }, [fetchImpl, intervalMs, restartKey]);
  return state;
}

/**
 * 補正サーバーの配信を `canvasRef` の canvas に動画で描く（`enabled` のあいだ。`url` か `resetKey` が変わったら繋ぎ直す）。
 * 席ごとの配信（`?seat=`）は見ているあいだだけ補正サーバーが作るので、要らなくなったら必ず切る（unmount で切れる）。
 */
export function useLiveVideo(canvasRef, url, enabled, resetKey = null) {
  useEffect(() => {
    if (!enabled || !url || !canvasRef.current) return undefined;
    const v = new LiveVideo({ url, canvas: canvasRef.current });
    v.start();
    return () => v.destroy();
  }, [canvasRef, url, enabled, resetKey]);
}

/** 読み終わってから差し替える静止画（ちらつかない）。`url` を変えるたびに読み、読めたらその url を返す */
export function useStill(url, enabled) {
  const [src, setSrc] = useState(null);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  useEffect(() => {
    if (!enabled || !url) return;
    const img = new Image();
    const full = liveUrl(url);
    img.onload = () => { if (alive.current) setSrc(full); };
    img.src = full;
  }, [url, enabled]);
  return src;
}
